<template>
  <div class="traveller-dob-form">
    <p class="dob-form-hint">Date of birth for each traveller — used for accurate Mapfre pricing.</p>
    <div
      v-for="(traveller, idx) in rows"
      :key="idx"
      class="dob-card"
    >
      <div class="dob-card-top">
        <span class="traveller-badge">{{ travellerBadge(idx) }}</span>
        <button
          v-if="rows.length > 1"
          type="button"
          class="policyholder-pill"
          :class="{ active: traveller.isMain, disabled: !canBePolicyholder(traveller) }"
          :disabled="!canBePolicyholder(traveller)"
          @click="setMain(idx)"
        >
          Policyholder
        </button>
      </div>
      <div class="pa-form-fields">
        <div class="input-group">
          <label class="input-label">{{ dobLabel(idx) }} <span class="req">*</span></label>
          <v-progress-linear
            v-if="selfCover && idx === 0 && selfDobLoading"
            indeterminate
            color="#00AEEF"
            class="tw-mb-2"
          />
          <v-text-field
            :value="traveller.dateOfBirth"
            type="date"
            :max="maxDob"
            :readonly="isSelfDobLocked(traveller, idx)"
            outlined
            dense
            :hide-details="!(selfCover && idx === 0 && selfDobError)"
            class="custom-input"
            :error-messages="selfCover && idx === 0 && selfDobError ? [selfDobError] : []"
            :error="!!(selfCover && idx === 0 && selfDobError)"
            @input="updateDob(idx, $event)"
            @change="updateDob(idx, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { emptyQuoteTraveller, isTravelAdult, reconcileTravelPolicyholder } from '@/utils/travelPricing';

export default {
  name: 'TravelTravellerDobForm',
  props: {
    travellers: { type: Array, default: () => [emptyQuoteTraveller(true)] },
    count: { type: Number, default: 1 },
    /** Customer is travelling — traveller 1 is the logged-in account holder. */
    selfCover: { type: Boolean, default: false },
    selfDobLoading: { type: Boolean, default: false },
    selfDobError: { type: String, default: '' },
    /** Offset for traveller numbering when row 1 is rendered elsewhere (e.g. customer self DOB). */
    travellerIndexOffset: { type: Number, default: 0 },
  },
  data() {
    return {
      maxDob: new Date().toISOString().slice(0, 10),
    };
  },
  computed: {
    rows() {
      const target = Math.min(Math.max(this.count || 1, 1), 10);
      let list = (this.travellers && this.travellers.length)
        ? this.travellers.map((t) => ({ ...t }))
        : [emptyQuoteTraveller(true)];
      if (list.length > target) list = list.slice(0, target);
      while (list.length < target) list.push(emptyQuoteTraveller(false));
      if (!list.some((r) => r.isMain)) list[0].isMain = true;
      return reconcileTravelPolicyholder(list);
    },
  },
  methods: {
    travellerBadge(idx) {
      const n = idx + 1 + (this.travellerIndexOffset || 0);
      if (this.selfCover && n === 1) return 'Traveller 1 (You)';
      return `Traveller ${n}`;
    },
    dobLabel(idx) {
      const n = idx + 1 + (this.travellerIndexOffset || 0);
      if (this.selfCover && n === 1) return 'Your date of birth';
      return 'Date of birth';
    },
    isSelfDobLocked(traveller, idx) {
      const n = idx + 1 + (this.travellerIndexOffset || 0);
      return this.selfCover && n === 1 && !!traveller?.identityFromIprs && !!traveller?.dateOfBirth;
    },
    canBePolicyholder(traveller) {
      if (!traveller?.dateOfBirth) return true;
      return isTravelAdult(traveller.dateOfBirth);
    },
    updateDob(idx, value) {
      if (this.isSelfDobLocked(this.rows[idx], idx)) return;
      const raw = value != null && typeof value === 'object' && value.target
        ? value.target.value
        : value;
      const dob = raw == null ? '' : String(raw).trim();
      if (this.rows[idx]?.dateOfBirth === dob) return;
      const updated = this.rows.map((t, i) => (
        i === idx ? { ...t, dateOfBirth: dob } : t
      ));
      const reconciled = reconcileTravelPolicyholder(updated, { preferIndex: idx });
      this.$emit('update:travellers', reconciled);
    },
    setMain(idx) {
      const row = this.rows[idx];
      if (row.dateOfBirth && !isTravelAdult(row.dateOfBirth)) {
        this.$emit('policyholder-error', 'Policyholder must be at least 18 years old.');
        return;
      }
      const reconciled = this.rows.map((traveller, i) => ({
        ...traveller,
        isMain: i === idx,
      }));
      this.$emit('update:travellers', reconciled);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/agent-pa-form.scss';

.dob-form-hint {
  font-size: 0.78rem;
  color: #64748B;
  margin: 0 0 12px;
  line-height: 1.4;
}

.dob-card {
  border: 1px solid #E8EDF2;
  border-radius: 10px;
  padding: 12px 14px 4px;
  margin-bottom: 10px;
  background: #FAFCFE;
}

.dob-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 10px;
}

.traveller-badge {
  font-size: 0.72rem;
  font-weight: 800;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #E0F2FE;
  padding: 4px 10px;
  border-radius: 20px;
}

.policyholder-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.76rem;
  font-weight: 700;
  color: #64748B;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1.5px solid #E2E8F0;
  background: #F8FAFC;
}

.policyholder-pill.active {
  border-color: #00AEEF;
  background: #E0F2FE;
  color: #1E4276;
}

.policyholder-pill.disabled,
.policyholder-pill:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.req { color: #DC2626; }
</style>
